import addProductSlangsHandler from "@bToC/Handlers/AddProductSlangsHandler";
import { ProductSlangDetailModel } from "@bToC/Services/ProductSlangListService";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext } from "@insite/client-framework/Components/ProductContext";
import { withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { HasHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        currentBillTo: getCurrentBillToState(state).value,
        session: state.context.session,
        location: getLocation(state),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    };
};

const mapDispatchToProps = {
    addProductSlangsHandler,
};

const enum fields {
    backgroundImage = "backgroundImage",
    text = "text",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundImage]: string;
        [fields.text]: string;
    };
}

type Props = OwnProps &
    WidgetProps &
    HasProductContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory;

interface AddProductSlangsWidgetStyles {
    wrapperStyles: GridContainerProps;
    mainTitle: InjectableCss;
    subTitle: InjectableCss;
    scrollableContainer: GridContainerProps;
    itemContainer: GridContainerProps;
    itemDisplay: GridContainerProps;
    deleteButton: ButtonProps;
    deleteButtonText: InjectableCss;
    linkEnabled: InjectableCss;
    linkDisabled: InjectableCss;
    textFieldContainer: GridContainerProps;
    textField: TextFieldPresentationProps;
    addButton: ButtonProps;
    addButtonText: InjectableCss;
    submitButton: ButtonProps;
}

const addProductSlangsWidgetStyles: AddProductSlangsWidgetStyles = {
    wrapperStyles: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    mainTitle: {
        css: css`
            font-size: 19px;
            font-weight: 700;
        `,
    },
    subTitle: {
        css: css`
            font-size: 12px;
            font-weight: 500;
            margin-bottom: 7px;
        `,
    },
    itemContainer: {
        css: css`
            display: flex;
            align-items: center;
            height: 38px;
        `,
    },
    scrollableContainer: {
        css: css`
            height: 65px;
            overflow-y: auto;
        `,
    },
    itemDisplay: {
        css: css`
            width: 100%;
            display: -webkit-box;
            display: -webkit-flex;
            display: -ms-flexbox;
            display: flex;
            -webkit-align-items: center;
            -webkit-box-align: center;
            -ms-flex-align: center;
            align-items: center;
            border: 2px solid;
            padding-left: 10px;
            border-right: none;
            height: 30px;
            font-size: 12px;
            font-weight: 700;
        `,
    },
    deleteButton: {
        css: css`
            margin: 10px 0px;
            border: 2px solid black;
            color: #ffffff;
            background: transparent;
            color: black;
            outline: black;
            border-left: none;
            min-height: 30px;
        `,
    },
    deleteButtonText: {
        css: css`
            font-weight: 700;
            font-size: 20px;
        `,
    },
    linkEnabled: {
        css: css`
            text-decoration: underline;
            color: rgb(1, 110, 175);
            font-size: 12px;
            cursor: pointer;
            margin: 3px 0px 6px 0px;
        `,
    },
    linkDisabled: {
        css: css`
            color: grey;
            font-size: 12px;
            cursor: not-allowed;
            margin: 3px 0px 6px 0px;
        `,
    },

    textFieldContainer: {
        css: css`
            display: flex;
            margin: 10px 0px;
            div[class^="FormFieldStyle"] {
                input {
                    height: 30px;
                    font-size: 12px;
                    font-weight: 700;
                    ::placeholder {
                        font-size: 11px;
                        opacity: 1; /* Firefox */
                    }
                    :-ms-input-placeholder {
                        /* Internet Explorer 10-11 */
                        font-size: 11px;
                    }
                    ::-ms-input-placeholder {
                        /* Microsoft Edge */
                        font-size: 11px;
                    }
                }
            }
        `,
    },
    textField: {
        css: css`
            height: 30px;
            font-size: 12px;
            font-weight: 700;
            border-bottom: 2px solid black;
        `,
    },
    addButton: {
        css: css`
            min-height: 30px;
            font-size: 12px;
            background: white;
            color: black;
            outline: none;
            border: 2px solid black;
        `,
    },
    addButtonText: {
        css: css`
            font-size: 12px;
            font-weight: 700;
        `,
    },
    submitButton: {
        variant: "primary",
    },
};

const styles = addProductSlangsWidgetStyles;

interface SlangItem {
    name: string;
    uniqueID: string;
}

const AddProductSlangsWidget: React.FC<Props> = ({
    addProductSlangsHandler,
    productContext,
    session,
    location,
    signInUrl,
}) => {
    const [showInputField, setshowInputField] = React.useState<boolean>(false);
    const [textInput, setTextInput] = React.useState<string>("");
    const [slangsList, setSlangsList] = React.useState<Array<SlangItem>>([]);
    const toaster = useContext(ToasterContext);
    const returnUrl = location.pathname + location.search;

    const handleChange = (event: React.SyntheticEvent<HTMLInputElement>): void => {
        setTextInput(event.currentTarget.value);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            handleAddButton();
        }
    };

    const handleAddButton = () => {
        if (textInput.trim().length === 0) {
            return;
        }
        const length = textInput.length;
        const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        let result = "";
        for (let i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        const newSlang = {
            name: textInput,
            uniqueID: result,
        };
        const updatedSlangsList = [...slangsList];
        updatedSlangsList.push(newSlang);
        setSlangsList(updatedSlangsList);
        setTextInput("");
        setshowInputField(false);
    };

    const handleShowInputField = () => {
        if (slangsList.length === 5) {
            return;
        }
        setshowInputField(!showInputField);
        setTextInput("");
    };

    const handleDeleteButton = (value: string) => {
        const updatedSlangsList = slangsList.filter(({ uniqueID }) => uniqueID !== value);
        setSlangsList(updatedSlangsList);
    };

    const handleSubmit = () => {
        const pathname = location.pathname;
        const paramModel: ProductSlangDetailModel = {
            ProductId: productContext.productInfo.productId,
            ProductNumber: productContext.product.productNumber,
            ProductCategoryPath: pathname,
            UserEmail: session.email,
            SlangTermValues: slangsList.map(({ name }) => name),
        };
        addProductSlangsHandler({
            model: paramModel,
            onComplete(result) {
                if (result.apiResult === "SlangTerms added successfully") {
                    setshowInputField(false);
                    setSlangsList([]);
                    toaster.addToast({
                        body: translate(`${result.apiResult}`),
                        messageType: "success",
                        timeoutLength: 5000,
                    });
                }
            },
        });
    };

    return (
        <>
            <StyledWrapper {...styles.wrapperStyles}>
                <Typography {...styles.mainTitle}>{translate(`What's your slang?`)}</Typography>
                <Typography {...styles.subTitle}>{translate("Submit what you call this part")}</Typography>
                {!session.isAuthenticated ? (
                    <Typography
                        onClick={() => {
                            window.location.href = `${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`;
                        }}
                        {...styles.linkEnabled}
                    >
                        {translate("Please Sign-in to Submit Slang Terms")}
                    </Typography>
                ) : (
                    <>
                        {slangsList.map(({ name, uniqueID }) => (
                            <StyledWrapper {...styles.itemContainer} key={uniqueID}>
                                <StyledWrapper {...styles.itemDisplay}>{name}</StyledWrapper>
                                <Button {...styles.deleteButton} onClick={() => handleDeleteButton(uniqueID)}>
                                    <Typography {...styles.deleteButtonText}>&#10005;</Typography>
                                </Button>
                            </StyledWrapper>
                        ))}
                        <Typography
                            {...(slangsList.length === 5 ? styles.linkDisabled : styles.linkEnabled)}
                            onClick={handleShowInputField}
                        >
                            {translate("Add another term")}
                        </Typography>
                        {showInputField && (
                            <>
                                <StyledWrapper {...styles.textFieldContainer}>
                                    <TextField
                                        autoFocus
                                        maxLength={22}
                                        onChange={handleChange}
                                        onKeyDown={handleKeyDown}
                                        value={textInput}
                                    />
                                    <Button {...styles.addButton} onClick={handleAddButton}>
                                        <Typography {...styles.addButtonText}>{translate("Add")}</Typography>
                                    </Button>
                                </StyledWrapper>
                            </>
                        )}
                        {slangsList.length !== 0 && (
                            <Button {...styles.submitButton} onClick={handleSubmit}>
                                {translate("Submit")}
                            </Button>
                        )}
                    </>
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProductContext(AddProductSlangsWidget)),
    definition: {
        displayName: "Add Product Slangs",
        group: "Product Details",
    },
};

export default widgetModule;
